'use client';

import React, { useEffect, useState, useRef } from 'react';

interface ComboIndicatorProps {
  multiplier: number;
  comboCount: number;
  windowActive: boolean;
  windowProgress: number;
}

export default function ComboIndicator({
  multiplier,
  comboCount,
  windowActive,
  windowProgress,
}: ComboIndicatorProps) {
  const [pulsing, setPulsing] = useState(false);
  const prevMultiplier = React.useRef(multiplier);

  useEffect(() => {
    if (multiplier !== prevMultiplier.current && multiplier > 1) {
      setPulsing(true);
      prevMultiplier.current = multiplier;
      const t = setTimeout(() => setPulsing(false), 350);
      return () => clearTimeout(t);
    }
  }, [multiplier]);

  if (comboCount === 0 && !windowActive) return null;

  const multiplierColor =
    multiplier >= 6 ? '#ff4d6d' :
    multiplier >= 4 ? '#ffd60a' :
    multiplier >= 2 ? '#00e5ff': '#ffffff';

  return (
    <div className="flex flex-col gap-1 slide-up-in">
      {/* Multiplier badge */}
      <div
        className={`hud-glass rounded-xl px-3 py-1.5 flex items-center gap-2 ${pulsing ? 'combo-pulse' : ''}`}
        style={{ borderColor: `${multiplierColor}33` }}
      >
        <span
          className="font-mono-game text-xl font-bold leading-none"
          style={{ color: multiplierColor }}
        >
          x{multiplier % 1 === 0 ? multiplier : multiplier.toFixed(1)}
        </span>
        <div className="flex flex-col">
          <span
            className="text-[10px] font-bold tracking-widest uppercase leading-none"
            style={{ color: multiplierColor, fontFamily: 'DM Sans, sans-serif' }}
          >
            Combo
          </span>
          <span
            className="text-[10px] font-medium leading-none mt-0.5"
            style={{ color: '#556677', fontFamily: 'DM Sans, sans-serif' }}
          >
            {comboCount} tricks
          </span>
        </div>
      </div>

      {/* Combo window bar */}
      {windowActive && (
        <div
          className="hud-glass rounded-full overflow-hidden"
          style={{ height: '4px', width: '100%' }}
        >
          <div
            className="h-full rounded-full combo-window-bar transition-all"
            style={{
              width: `${windowProgress * 100}%`,
              background: `linear-gradient(90deg, ${multiplierColor}, ${multiplierColor}88)`,
              boxShadow: `0 0 6px ${multiplierColor}`,
            }}
          />
        </div>
      )}
    </div>
  );
}